import { useEffect, useRef, type KeyboardEvent } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useScope } from "@/api/queries";
import { useDashboardParams } from "@/state/useDashboardParams";
import { Breadcrumb, currentRouteCrumb, routeCrumbAt } from "@/components/drill";
import { PageHeader } from "@/features/drill/common";
import { useT } from "@/i18n/I18nProvider";
import { CRDCatalog } from "./CRDCatalog";
import { ResourceExplorer } from "./ResourceExplorer";
import { ResourceMapView } from "./ResourceMapView";

/**
 * Resources 진입 화면 (ADR 0018 · ADR 0020)
 * --------------------------------------------------------------------------
 * 탭 네 개로 리소스 작업의 진입점을 한곳에 모읍니다.
 *
 * - **Explorer** — 이 화면 안에서 렌더링하는 조회 전용 탐색기.
 * - **Custom Resource Definitions** — 이 클러스터의 CRD 정의 카탈로그(ADR 0065 C1).
 *   **새 엔드포인트가 없습니다** — 기존 목록·상세를 CRD GVR로 부를 뿐입니다. 서버가
 *   discovery로 대조한 "Explorer allowlist 포함 여부"를 행마다 보여 주고, 미포함이면
 *   운영자가 붙일 `extraResources` 한 줄을 복사하게 합니다(브라우저에서 allowlist를
 *   바꾸는 경로는 없습니다).
 * - **Resource Map** — Explorer에서 고른 항목을 focus로 하는 조회 전용 관계
 *   그래프. 같은 URL 상태(res·item)를 그대로 focus로 쓰므로 별도 조회가 없고,
 *   탭 전환은 `tab` 하나만 바꿉니다. 최상위 라우트·nav 항목을 추가하지 않습니다.
 * - **Deployments / Secrets** — 기존 `/deployments`·`/secrets` 화면으로 이동합니다.
 *   기존 라우트·화면·좌측 nav 항목은 그대로 두고 여기서 **추가 진입점만** 제공합니다.
 *   리다이렉트하지 않으므로 기존 링크와 북마크는 계속 그대로 동작합니다.
 *
 * 탭은 WAI-ARIA tabs 패턴을 따릅니다 — 좌우 화살표·Home/End로 이동하고,
 * 선택 상태는 색이 아니라 `aria-selected`와 텍스트로 전달합니다.
 */

type TabId = "explorer" | "crds" | "map" | "deployments" | "secrets";
type InternalTabId = "explorer" | "crds" | "map";

const TABS: { id: TabId; label: string; to?: string }[] = [
  { id: "explorer", label: "Explorer" },
  /* 라벨은 i18n 조각에서 옵니다 — 이 표의 문자열은 fallback입니다. */
  { id: "crds", label: "Custom Resource Definitions" },
  { id: "map", label: "Resource Map" },
  { id: "deployments", label: "Deployments", to: "/deployments" },
  { id: "secrets", label: "Secrets", to: "/secrets" },
];

export function ResourcesView() {
  const { search } = useLocation();
  const [params, setParams] = useSearchParams();
  const { clusterId } = useDashboardParams();
  const scope = useScope();
  const canExplore = scope.data?.canExploreResources ?? false;
  const canManage = scope.data?.canManageWorkloads ?? false;
  /* Resource Map은 서버 capability(canUseResourceMap — RESOURCE_MAP_ENABLED 게이트)가
     정본입니다(ADR 0020 · #8 M4-I16). scope 로딩 중·부재·꺼진 배포에서는 탭도 패널도
     만들지 않습니다 — ResourceMapView가 마운트되지 않으므로 map 요청은 0회입니다. */
  const canUseMap = scope.data?.canUseResourceMap ?? false;
  const scopeLoaded = scope.data !== undefined;
  const wantsMap = params.get("tab") === "map";
  const wantsCRDs = params.get("tab") === "crds";
  const tabRefs = useRef<(HTMLElement | null)[]>([]);
  const { t } = useT("resources");

  /* 내부 선택 탭은 typed 질의 값 `tab=map` 하나입니다. 없거나 모르는 값이면
     Explorer로 fail-closed합니다 — 모르는 값으로 빈 패널을 열지 않습니다.
     Map capability가 없으면 `tab=map`도 Explorer입니다. */
  const internalTab: InternalTabId = wantsMap && canUseMap ? "map" : wantsCRDs ? "crds" : "explorer";

  /* 꺼진 배포의 `tab=map`은 **scope가 도착한 뒤에만** URL에서 지웁니다(replace).
     로딩 중에는 URL을 건드리지 않아 켜진 배포의 deep link가 살아남습니다. */
  useEffect(() => {
    if (!scopeLoaded || canUseMap || !wantsMap) return;
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("tab");
        return p;
      },
      { replace: true },
    );
  }, [scopeLoaded, canUseMap, wantsMap, setParams]);

  /* 관리 권한이 없으면 관리 탭은 아예 만들지 않습니다 — 눌러도 403인 탭은 소음입니다.
     기존 nav의 관리 그룹 노출 조건과 같은 값을 씁니다. Map 탭은 서버 capability가 있을 때만. */
  /* CRD 정의 탭은 Explorer capability만 요구합니다 — 서버가 allowlist에서 뺀
     배포에서는 탭 안에서 그 사실을 말합니다(빈 표로 뭉개지 않습니다). */
  const tabs = TABS.filter(
    (tab) =>
      tab.id === "explorer" || tab.id === "crds" || (tab.id === "map" ? canUseMap : canManage),
  );

  /* Explorer ↔ Resource Map 전환은 `tab`만 바꿉니다. cluster·res·item과 무관한
     질의 값은 그대로 남아 Explorer 선택이 곧 그래프 focus가 됩니다. */
  const selectInternal = (id: InternalTabId) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (id === "map") p.set("tab", "map");
        else if (id === "crds") p.set("tab", "crds");
        else p.delete("tab");
        return p;
      },
      { replace: true },
    );

  const onTabKeyDown = (event: KeyboardEvent, index: number) => {
    const keys: Record<string, number> = {
      ArrowRight: (index + 1) % tabs.length,
      ArrowLeft: (index - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    };
    const next = keys[event.key];
    if (next === undefined) return;
    event.preventDefault();
    tabRefs.current[next]?.focus();
  };

  return (
    <div className="page">
      <PageHeader
        title="Resources"
        subtitle={clusterId}
        crumbs={<Breadcrumb items={[routeCrumbAt("overview", "/", search), currentRouteCrumb("resources")]} />}
      />

      <div className="chips resource-tabs" role="tablist" aria-label="리소스 작업">
        {tabs.map((tab, index) => {
          const selected = tab.id === internalTab;
          const shared = {
            role: "tab" as const,
            id: `resources-tab-${tab.id}`,
            className: "chip",
            "aria-selected": selected,
            tabIndex: selected ? 0 : -1,
            onKeyDown: (e: KeyboardEvent) => onTabKeyDown(e, index),
          };
          if (!tab.to) {
            return (
              <button
                key={tab.id}
                type="button"
                {...shared}
                aria-controls={`resources-panel-${tab.id}`}
                onClick={() => selectInternal(tab.id as InternalTabId)}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
              >
                {tab.id === "crds" ? t("resources.title") : tab.label}
              </button>
            );
          }
          return (
            <Link
              key={tab.id}
              to={{ pathname: tab.to, search }}
              {...shared}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
            >
              {tab.label}
              <span className="visually-hidden"> — 전용 화면으로 이동</span>
            </Link>
          );
        })}
      </div>

      {/* 선택된 내부 패널 하나만 렌더링합니다 — 숨긴 패널이 요청을 만들면 안 됩니다. */}
      <div
        id={`resources-panel-${internalTab}`}
        role="tabpanel"
        aria-labelledby={`resources-tab-${internalTab}`}
        tabIndex={-1}
      >
        {canExplore ? (
          internalTab === "map" ? (
            <ResourceMapView />
          ) : internalTab === "crds" ? (
            <CRDCatalog />
          ) : (
            <ResourceExplorer />
          )
        ) : (
          <section className="panel">
            <div className="panel__body">
              <div className="state state--forbidden" role="alert">
                <span className="state__glyph" aria-hidden="true">
                  !
                </span>
                <span className="state__title">리소스 탐색 권한이 없습니다</span>
                <span className="state__detail">
                  이 기능은 platform.admin 전용입니다. 데이터가 없는 것이 아니라 조회가 거절되었습니다.
                </span>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
